import {
  BookingFromApi,
  ProcessedScanResult,
  ScanResultType
} from "../../components/Pages/Diary/SpaceScanner/SpaceScanner.types";
import {
  isUnassignedResult,
  LOCKED_OR_EXCLUDED_RESULT_TYPES,
  RESULT_TYPE_TEXT
} from "../../components/Pages/Diary/SpaceScanner/SpaceScannerConstants";
import { BookableArea } from "../../types/venues";
import { parseHour } from "./timeUtils";

export type ContextualHelpActionType = "settings" | "documentation" | "info";

export interface ContextualHelpResult {
  suggestion: string;
  actionType: ContextualHelpActionType;
  actionLabel?: string;
  actionPath?: string;
}

interface OptimisationScoreResult {
  score: number;
  label: string;
  explanation: string;
}

export type CapacitySeverity = "info" | "warning" | "critical";

export interface CapacityInsight {
  timePeriod: string;
  capacityPercentage: number;
  suggestion: string;
  severity: CapacitySeverity;
}

export type ConstraintPriority = "high" | "medium" | "low";

export interface ConstraintTradeoff {
  bookingId: string;
  constraints: string[];
  explanation: string;
  priority: ConstraintPriority;
}

/**
 * Calculates an optimisation score based on scan results.
 * The score represents how well bookings are placed after optimisation.
 */
export const calculateOptimisationScore = (
  results: ProcessedScanResult[],
  context: "preview" | "applied" = "applied"
): OptimisationScoreResult => {
  const total = results.length;

  if (total === 0) {
    return {
      score: 0,
      label: "No Data",
      explanation: "No bookings were included in this scan."
    };
  }

  const lockedOrExcluded = results.filter(
    (r) => LOCKED_OR_EXCLUDED_RESULT_TYPES.includes(r.result) || r.shuffleLocked
  ).length;

  const movedToBetter = results.filter(
    (r) => r.moved && !r.shuffleLocked && !isUnassignedResult(r.result)
  ).length;

  const noBetterArea = results.filter(
    (r) =>
      !r.moved &&
      !r.shuffleLocked &&
      (r.result === "no_better_area" || r.result === "preferred_area_found")
  ).length;

  const assessable = total - lockedOrExcluded;

  if (assessable === 0) {
    return {
      score: 100,
      label: "N/A",
      explanation:
        "All bookings are locked or excluded and cannot be optimised."
    };
  }

  const score = Math.round(((movedToBetter + noBetterArea) / assessable) * 100);

  const label = getScoreLabel(score);
  const optimalCount = movedToBetter + noBetterArea;
  const isPlural = movedToBetter !== 1;
  const isPreview = context === "preview";

  let movedVerb: string;
  if (isPreview) {
    movedVerb = isPlural ? "s will be" : " will be";
  } else {
    movedVerb = isPlural ? "s were" : " was";
  }
  const unassignedCount = results.filter((r) =>
    isUnassignedResult(r.result)
  ).length;
  const stayCount = total - movedToBetter - unassignedCount - lockedOrExcluded;
  const stayVerb = context === "preview" ? "will stay" : "stayed";
  let staySuffix = "";
  if (stayCount > 0) {
    staySuffix = ` ${stayCount} ${stayVerb} in place`;
    if (lockedOrExcluded > 0) {
      staySuffix += ` (${lockedOrExcluded} locked or excluded)`;
    }
    staySuffix += ".";
  }
  const explanation = `${optimalCount} of ${assessable} bookings are optimally placed. ${movedToBetter} booking${movedVerb} moved to better-fitting tables.${staySuffix}`;

  return { score, label, explanation };
};

/**
 * Returns a human-readable label for the given score.
 */
const getScoreLabel = (score: number): string => {
  if (score >= 90) return "Excellent";
  if (score >= 75) return "Good";
  if (score >= 50) return "Fair";
  return "Needs Attention";
};

/**
 * Generates a concise, human-readable explanation for why a booking was moved
 * or kept in place. Always uses structured data (areas, capacity, party size)
 * rather than raw backend debug text.
 */
export const explainBookingMove = (result: ProcessedScanResult): string => {
  if (
    !result.moved &&
    (result.result === "no_better_area" ||
      result.result === "preferred_area_found")
  ) {
    return "This booking is already optimally placed.";
  }

  return buildFallbackExplanation(result);
};

/**
 * Returns the filtered backend explanation lines for display in a
 * "More info" section. Debug-level entries are stripped out.
 * Returns an empty array when no meaningful detail is available.
 */
export const getExplanationDetails = (
  result: ProcessedScanResult
): string[] => {
  if (!result.explain || result.explain.length === 0) {
    return [];
  }

  return filterExplanationLines(result.explain);
};

/**
 * Filters verbose backend explanation steps, discarding debug-level lines
 * (overlap checks, empty-table scans) and keeping only conclusion entries.
 */
const filterExplanationLines = (lines: string[]): string[] => {
  const debugPatterns = [
    /^Checking Table/i,
    /Area isn't empty/i,
    /Areas aren't empty/i,
    /Area isn't available/i,
    /doesn't overlap/i,
    /^Found booking/i,
    /^Booking DMN-/i,
    /^overlap/i
  ];

  return lines.filter(
    (line) => !debugPatterns.some((pattern) => pattern.test(line.trim()))
  );
};

/**
 * Constructs a fallback explanation from available booking and area data.
 */
const buildFallbackExplanation = (result: ProcessedScanResult): string => {
  if (!result.moved) {
    return getNotMovedExplanation(result);
  }

  const partySize = result.partySize ?? result.booking.num_people;
  const previousAreaName =
    result.previousAreas?.[0]?.name ?? "their current table";
  const newAreaName = result.newAreas?.[0]?.name ?? "a new table";

  const previousCapacity = result.previousAreaCapacity;
  const newCapacity = result.areaCapacity;

  if (previousCapacity && newCapacity) {
    return `Moving this ${partySize}-person party from ${previousAreaName} (seats ${previousCapacity}) to ${newAreaName} (seats ${newCapacity}) frees up ${previousAreaName} for larger bookings.`;
  }

  return `Consider moving this ${partySize}-person party from ${previousAreaName} to ${newAreaName} for a better fit.`;
};

/**
 * Returns an explanation for bookings that are not being moved.
 */
const getNotMovedExplanation = (result: ProcessedScanResult): string => {
  return (
    RESULT_TYPE_TEXT[result.result]?.explanation ??
    "No changes suggested for this booking."
  );
};

/**
 * Returns an actionable suggestion based on the unassigned reason type.
 * Used as the default fallback when contextual help is not available.
 */
export const getDefaultUnassignedSuggestion = (
  type: ScanResultType
): string => {
  switch (type) {
    case "unassigned_no_tables_configured":
      return "Consider configuring tables for this booking type in venue settings.";
    case "unassigned_table_too_small":
      return "Consider adding larger tables or splitting the party across multiple bookings.";
    case "unassigned_rule_conflict":
      return "Review booking rules in venue settings to resolve conflicts.";
    case "unassigned_no_availability":
      return "Consider adjusting booking times or adding more tables for this time slot.";
    default:
      return "Review venue configuration to resolve this issue.";
  }
};

/**
 * Returns contextual, AI-powered help for an unassigned booking result.
 * Uses an advisory tone and provides actionable guidance based on the
 * specific reason the booking could not be assigned.
 */
export const getContextualHelp = (
  results: ProcessedScanResult[],
  venueConfig?: { maxTableCapacity?: number }
): ContextualHelpResult => {
  if (results.length === 0) {
    return {
      suggestion: "Review your venue configuration to help resolve this issue.",
      actionType: "info"
    };
  }

  const resultType: ScanResultType = results[0].result;

  switch (resultType) {
    case "unassigned_table_too_small":
      return buildTableTooSmallHelp(results, venueConfig);
    case "unassigned_no_tables_configured":
      return buildNoTablesConfiguredHelp(results);
    case "unassigned_rule_conflict":
      return buildRuleConflictHelp(results);
    case "unassigned_no_availability":
      return buildNoAvailabilityHelp(results);
    default:
      return {
        suggestion: getDefaultUnassignedSuggestion(resultType),
        actionType: "info"
      };
  }
};

const buildTableTooSmallHelp = (
  results: ProcessedScanResult[],
  venueConfig?: { maxTableCapacity?: number }
): ContextualHelpResult => {
  const partySizes = results.map((r) => r.booking.num_people);
  const minParty = Math.min(...partySizes);
  const maxParty = Math.max(...partySizes);
  const maxCapacity = venueConfig?.maxTableCapacity;

  const partySizeText =
    minParty === maxParty
      ? `${minParty} guests`
      : `${minParty}–${maxParty} guests`;

  const capacityDetail = maxCapacity
    ? ` Your largest configured table seats ${maxCapacity}.`
    : "";

  const bookingWord = results.length === 1 ? "booking needs" : "bookings need";

  return {
    suggestion: `${results.length} ${bookingWord} tables for ${partySizeText}.${capacityDetail} Consider configuring joined tables to accommodate larger parties.`,
    actionType: "info"
  };
};

/**
 * Formats a list of items into a display string.
 * Returns the fallback when empty, the single item when one,
 * or a comma-separated string for multiple items.
 */
const formatListText = (items: string[], fallback: string): string => {
  if (items.length === 0) return fallback;
  if (items.length === 1) return items[0];
  return items.join(", ");
};

const buildNoTablesConfiguredHelp = (
  results: ProcessedScanResult[]
): ContextualHelpResult => {
  const typeNames = [
    ...new Set(
      results
        .map((r) => r.booking.type?.name)
        .filter((name): name is string => Boolean(name))
    )
  ];

  const areaNames = [
    ...new Set(
      results
        .flatMap((r) => r.previousAreas?.map((a) => a.name) ?? [])
        .filter(Boolean)
    )
  ];

  const typeText = formatListText(typeNames, "this");

  const areaText =
    areaNames.length > 0 ? ` in ${areaNames.join(", ")}` : " in this area";

  return {
    suggestion: `The '${typeText}' booking type isn't configured to use any tables${areaText}. Adding table assignments could resolve this.`,
    actionType: "settings",
    actionLabel: "Booking type settings",
    actionPath: "/settings/booking-types"
  };
};

const buildRuleConflictHelp = (
  results: ProcessedScanResult[]
): ContextualHelpResult => {
  const bookingWord =
    results.length === 1
      ? "this assignment"
      : `these ${results.length} assignments`;

  return {
    suggestion: `A booking rule is preventing ${bookingWord}. Check your area rules for conflicts with this booking type or time slot.`,
    actionType: "settings",
    actionLabel: "Rules settings",
    actionPath: "/settings/rules"
  };
};

const buildNoAvailabilityHelp = (
  results: ProcessedScanResult[]
): ContextualHelpResult => {
  const timeSlots = [
    ...new Set(results.map((r) => r.booking.time).filter(Boolean))
  ];

  const timeText = formatListText(timeSlots, "the requested times");

  return {
    suggestion: `All suitable tables are occupied at ${timeText}. Consider adjusting booking times or adding capacity.`,
    actionType: "info"
  };
};

/**
 * Formats a 24h time string (e.g. "19:00") into a readable hourly slot label
 * (e.g. "7\u20138pm"). Falls back to the raw value when parsing fails.
 */
const formatHourSlot = (hour: number): string => {
  const startSuffix = hour >= 12 ? "pm" : "am";
  const start12 = hour % 12 || 12;
  const endHour = hour + 1;
  const endSuffix = endHour >= 12 ? "pm" : "am";
  const end12 = endHour % 12 || 12;

  if (startSuffix === endSuffix) {
    return `${start12}\u2013${end12}${endSuffix}`;
  }
  return `${start12}${startSuffix}\u2013${end12}${endSuffix}`;
};

/**
 * Returns the capacity severity based on the percentage.
 */
const getCapacitySeverity = (percentage: number): CapacitySeverity => {
  if (percentage >= 95) return "critical";
  if (percentage >= 90) return "warning";
  return "info";
};

interface HourlySlot {
  total: number;
  assigned: number;
  unassigned: number;
}

/**
 * Groups scan results into hourly slots, tallying totals, assigned and
 * unassigned counts for each hour.
 */
const buildSlotMap = (
  results: ProcessedScanResult[]
): Map<number, HourlySlot> => {
  const slotMap = new Map<number, HourlySlot>();

  for (const r of results) {
    const hour = parseHour(r.booking.time);
    if (hour === undefined) continue;

    const entry = slotMap.get(hour) ?? { total: 0, assigned: 0, unassigned: 0 };
    entry.total += 1;

    if (isUnassignedResult(r.result)) {
      entry.unassigned += 1;
    } else {
      entry.assigned += 1;
    }

    slotMap.set(hour, entry);
  }

  return slotMap;
};

/**
 * Calculates what percentage of capacity a slot is using.
 */
const calculateSlotCapacity = (
  slot: HourlySlot,
  maxCapacity: number | undefined
): number =>
  maxCapacity
    ? Math.round((slot.total / maxCapacity) * 100)
    : Math.round((slot.assigned / slot.total) * 100);

/**
 * Builds a capacity insight for a high-usage time slot.
 */
const buildHighCapacityInsight = (
  timePeriod: string,
  capacityPercentage: number
): CapacityInsight => ({
  timePeriod,
  capacityPercentage,
  suggestion: `Your venue is at ${capacityPercentage}% capacity between ${timePeriod}. Consider adding temporary seating or adjusting booking configurations.`,
  severity: getCapacitySeverity(capacityPercentage)
});

/**
 * Builds a capacity insight for unassigned bookings in a time slot.
 */
const buildUnassignedInsight = (
  timePeriod: string,
  capacityPercentage: number,
  unassigned: number
): CapacityInsight => {
  const bookingLabel = unassigned !== 1 ? "bookings" : "booking";
  const periodLabel =
    capacityPercentage >= 90 ? "your busiest period" : "a busy period";

  return {
    timePeriod,
    capacityPercentage,
    suggestion: `${unassigned} ${bookingLabel} couldn't be placed during the ${timePeriod} slot. This is ${periodLabel}.`,
    severity: unassigned >= 3 ? "warning" : "info"
  };
};

/**
 * Analyses scan results by time period and generates capacity insights.
 * Groups bookings into hourly slots, calculates how full each slot is
 * relative to total venue capacity, and returns actionable suggestions.
 */
export const analyzeCapacityIssues = (
  results: ProcessedScanResult[],
  venueConfig?: { bookable_areas?: BookableArea[] }
): CapacityInsight[] => {
  if (results.length === 0) {
    return [];
  }

  const slotMap = buildSlotMap(results);
  const maxCapacity = deriveMaxTableCapacity(venueConfig?.bookable_areas);
  const insights: CapacityInsight[] = [];
  const sortedHours = [...slotMap.keys()].sort((a, b) => a - b);

  for (const hour of sortedHours) {
    const slot = slotMap.get(hour)!;
    const timePeriod = formatHourSlot(hour);
    const capacityPercentage = calculateSlotCapacity(slot, maxCapacity);

    if (capacityPercentage >= 90) {
      insights.push(buildHighCapacityInsight(timePeriod, capacityPercentage));
    }

    if (slot.unassigned > 0) {
      insights.push(
        buildUnassignedInsight(timePeriod, capacityPercentage, slot.unassigned)
      );
    }
  }

  return insights;
};

/**
 * Derives the total table capacity from bookable areas.
 * Sums max_people across all configured areas.
 */
const deriveMaxTableCapacity = (areas?: BookableArea[]): number | undefined => {
  if (!areas || areas.length === 0) return undefined;
  return areas.reduce((sum, area) => sum + (area.max_people ?? 0), 0);
};

/**
 * Checks whether a booking has a VIP label.
 */
const isVipBooking = (booking: BookingFromApi): boolean =>
  Boolean(
    (booking as BookingFromApi & { labels?: string[] }).labels?.find(
      (label: string) => label === "VIP"
    )
  );

/**
 * Checks whether a booking has dietary requirements.
 */
const hasDietaryRequirements = (booking: BookingFromApi): boolean => {
  const extended = booking as BookingFromApi & {
    dietaryRequirements?: unknown;
  };
  const val = extended.dietaryRequirements;
  if (typeof val === "string") return val.trim() !== "";
  if (Array.isArray(val)) return val.length > 0;
  return Boolean(val);
};

/**
 * Checks whether a booking has special (accessibility) requirements.
 */
const hasSpecialRequirements = (booking: BookingFromApi): boolean => {
  const extended = booking as BookingFromApi & {
    specialRequirements?: unknown;
  };
  const val = extended.specialRequirements;
  if (typeof val === "string") return val.trim() !== "";
  if (Array.isArray(val)) return val.length > 0;
  return Boolean(val);
};

/**
 * Determines the priority of a constraint trade-off based on the types
 * of constraints involved.
 */
const getConstraintPriority = (constraints: string[]): ConstraintPriority => {
  if (constraints.includes("accessibility")) return "high";
  if (constraints.includes("vip")) return "high";
  return "medium";
};

/**
 * Formats a guest count label, e.g. "3 guests" or "1 guest".
 */
const formatGuestCount = (count: number): string =>
  `${count} guest${count !== 1 ? "s" : ""}`;

/**
 * Builds a VIP-specific trade-off explanation when applicable.
 */
const buildVipExplanation = (
  name: string,
  guests: number,
  areaName: string | undefined,
  moved: boolean
): string | undefined => {
  const guestLabel = formatGuestCount(guests);
  const fromArea = areaName ? ` from ${areaName}` : "";

  if (moved) {
    return `VIP booking for ${name} (${guestLabel}) was moved${fromArea}, which may affect their preferred placement.`;
  }

  const location = areaName ?? "their current table";
  return `VIP booking for ${name} (${guestLabel}) was kept at ${location} to preserve their preferred placement.`;
};

/**
 * Builds an accessibility-specific trade-off explanation when applicable.
 */
const buildAccessibilityExplanation = (
  name: string,
  result: ProcessedScanResult
): string | undefined => {
  if (isUnassignedResult(result.result)) {
    return `Booking for ${name} has accessibility requirements but couldn't be placed. Review accessible table availability.`;
  }

  if (result.moved) {
    return `Booking for ${name} has accessibility requirements and was moved, which may affect access needs.`;
  }

  return undefined;
};

/**
 * Builds a human-readable explanation for a constraint trade-off.
 */
const buildTradeoffExplanation = (
  result: ProcessedScanResult,
  constraints: string[]
): string => {
  const { booking } = result;
  const name = `${booking.first_name} ${booking.last_name}`.trim();
  const guests = booking.num_people;
  const areaName =
    result.previousAreas?.[0]?.name ?? result.newAreas?.[0]?.name;

  const parts: string[] = [];

  if (constraints.includes("vip")) {
    const vipText = buildVipExplanation(name, guests, areaName, result.moved);
    if (vipText) parts.push(vipText);
  }

  if (constraints.includes("dietary") && result.moved) {
    parts.push(
      `Booking for ${name} has dietary requirements and was moved away from their preferred area.`
    );
  }

  if (constraints.includes("accessibility")) {
    const accessText = buildAccessibilityExplanation(name, result);
    if (accessText) parts.push(accessText);
  }

  return parts.length > 0
    ? parts.join(" ")
    : `Booking for ${name} has constraints (${constraints.join(", ")}) that affected placement.`;
};

/**
 * Analyses scan results for constraint trade-offs. Identifies bookings
 * with VIP status, dietary requirements or accessibility needs that were
 * affected by the optimisation and explains the trade-offs involved.
 */
export const analyzeConstraintTradeoffs = (
  results: ProcessedScanResult[]
): ConstraintTradeoff[] => {
  const tradeoffs: ConstraintTradeoff[] = [];

  for (const r of results) {
    const constraints: string[] = [];

    if (isVipBooking(r.booking)) {
      constraints.push("vip");
    }
    if (hasDietaryRequirements(r.booking)) {
      constraints.push("dietary");
    }
    if (hasSpecialRequirements(r.booking)) {
      constraints.push("accessibility");
    }

    if (constraints.length === 0) continue;

    const isAffected = r.moved || isUnassignedResult(r.result);

    if (!isAffected) continue;

    tradeoffs.push({
      bookingId: r.booking._id,
      constraints,
      explanation: buildTradeoffExplanation(r, constraints),
      priority: getConstraintPriority(constraints)
    });
  }

  return tradeoffs;
};
